import { useState } from 'react';
import { aboutContent } from '@/mocks/data';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('website_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      return;
    }

    setFormStatus('loading');
    setFormError('');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9q4h7nnahb7382ov5v0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(
          Array.from(formData.entries())
            .filter(([key]) => key !== 'website_alt')
            .map(([k, v]) => [k, v as string])
        ).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        setFormStatus('error');
        setFormError(parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || '提交失败，请稍后再试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络后重试');
    }
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于我们 - 鲍鱼tv跳转导航',
            description: aboutContent.brandIntro,
            url: 'https://baoyutv-tiaozhuan.com/about',
          }),
        }}
      />

      <main>
        <section className="py-8 md:py-12 bg-background-100/70">
          <div className="container-site">
            <Breadcrumb />
            <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mt-2">
              关于我们
            </h1>
          </div>
        </section>

        {/* Brand Introduction */}
        <section className="py-14 md:py-20" id="about" aria-label="品牌介绍">
          <div className="container-site">
            <div className="max-w-3xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <span className="w-10 h-10 flex items-center justify-center bg-primary-500 text-background-50 rounded-xl text-lg font-bold">
                  TV
                </span>
                <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900">
                  {aboutContent.brandName}
                </h2>
              </div>
              <p className="text-base text-foreground-600 leading-relaxed mb-6">
                {aboutContent.brandIntro}
              </p>
              <p className="text-base text-foreground-600 leading-relaxed">
                {aboutContent.mission}
              </p>
            </div>
          </div>
        </section>

        {/* Reputation */}
        <section className="py-14 md:py-20 bg-background-100/70" id="reputation" aria-label="信誉展示">
          <div className="container-site">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-3">
                我们的信誉
              </h2>
              <p className="text-sm text-foreground-500 max-w-xl mx-auto">
                用数据说话，鲍鱼tv跳转导航值得信赖
              </p>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 max-w-3xl mx-auto">
              {aboutContent.reputation.map((item) => (
                <div
                  key={item.label}
                  className="bg-background-50 rounded-xl border border-background-200/70 p-5 md:p-6 text-center"
                >
                  <div className="text-2xl md:text-3xl font-bold font-heading text-accent-600 mb-1">
                    {item.value}
                  </div>
                  <div className="text-sm text-foreground-500">{item.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="py-14 md:py-20" id="contact" aria-label="联系我们">
          <div className="container-site">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-3">
                  联系我们
                </h2>
                <p className="text-sm text-foreground-500 max-w-xl mx-auto">
                  如有任何关于鲍鱼tv跳转的问题或建议，欢迎通过以下方式联系我们
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Contact Info */}
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-accent-100 flex-shrink-0">
                      <i className="ri-mail-line text-accent-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800 mb-0.5">电子邮箱</p>
                      <a href={`mailto:${aboutContent.contact.email}`} className="text-sm text-accent-600 hover:text-accent-700 transition-colors duration-200">
                        {aboutContent.contact.email}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-100 flex-shrink-0">
                      <i className="ri-wechat-line text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800 mb-0.5">微信</p>
                      <p className="text-sm text-foreground-500">{aboutContent.contact.wechat}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-secondary-100 flex-shrink-0">
                      <i className="ri-qq-line text-secondary-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800 mb-0.5">QQ</p>
                      <p className="text-sm text-foreground-500">{aboutContent.contact.qq}</p>
                    </div>
                  </div>
                </div>

                {/* Form */}
                <div className="bg-background-50 rounded-xl border border-background-200/70 p-5 md:p-6">
                  <h3 className="text-base font-semibold font-heading text-foreground-900 mb-4">
                    发送消息
                  </h3>
                  <form onSubmit={handleSubmit} data-readdy-form="" className="flex flex-col gap-4">
                    <input
                      type="text"
                      name="website_alt"
                      className="website-url-field"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        姓名
                      </label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        className="w-full px-4 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-100 transition-all duration-200"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        邮箱
                      </label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        className="w-full px-4 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-100 transition-all duration-200"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        留言内容
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        rows={4}
                        maxLength={500}
                        className="w-full px-4 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-100 transition-all duration-200 resize-none"
                        placeholder="请输入您的留言内容（最多500字）"
                      ></textarea>
                    </div>
                    <button
                      type="submit"
                      disabled={formStatus === 'loading'}
                      className="w-full py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium rounded-lg transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap text-sm"
                    >
                      {formStatus === 'loading' ? '提交中...' : '发送消息'}
                    </button>

                    {formStatus === 'success' && (
                      <p className="text-sm text-accent-600 text-center bg-accent-50 py-2 px-3 rounded-lg">
                        消息已成功发送，我们会尽快回复您！
                      </p>
                    )}
                    {formStatus === 'error' && (
                      <p className="text-sm text-red-500 text-center bg-red-50 py-2 px-3 rounded-lg">
                        {formError}
                      </p>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}